// /pages/MaintenanceHistory/index.tsx
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
    Box, Heading, Text, HStack, VStack, InputGroup, Input, 
    InputRightElement, IconButton, Card, CardHeader, CardBody, 
    Icon
} from '@chakra-ui/react';
import { Search, ChevronLeft } from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { apiClient } from '@/lib/api/client';
import { useToastNotification } from '@/lib/utils/useToastNotification';
import { DataTable, type DataTableRef, type DataTableOptions } from '@/components/tables';
import { useAccessCheck } from '@/hooks/useAccess';
import { useThemeColors } from '@/hooks/useThemeColors';
import { ActionButtons } from '@/components/common/ActionButtons';


const MaintenanceHistoryPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const access = useAccessCheck();
    const tableRef = useRef<DataTableRef>(null);
    const searchRef = useRef<HTMLInputElement>(null);
    const colors = useThemeColors();

    const [truckPlate, setTruckPlate] = useState('');
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        const fetchTruck = async () => {
            try {
                const response = await apiClient.get(`/trucks/get-truck-by-id/${id}`);
                setTruckPlate(response.data.plate_number);
            } catch (error) {
                console.error(error);
            }
        };
        if (id) fetchTruck();
    }, [id]);

    // 🌟 1. Date Formatter: "December 18, 2025"
    const renderFormattedDate = (value: string) => {
        if (!value) return '-';
        const date = new Date(value);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    };

    // 🌟 2. Total Cost Calculator (Fixes the blank column)
    const renderTotalCost = (_: any, row: any) => {
        const qty = Number(row.quantity) || 0;
        const price = Number(row.price) || 0;
        const total = qty * price;
        return `P ${total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const tableOptions: DataTableOptions = useMemo(() => ({
        dataFields: {
            receipt_number: { name: 'Receipt', withSort: true, maxWidth: '150px' },
            article: { name: 'Article', withSort: false },
            quantity: { name: 'Qty', withSort: false, maxWidth: '80px' },
            price: { name: 'Price', withSort: false, maxWidth: '120px' },
            total: { name: 'Total Cost', withSort: false, maxWidth: '150px' }, // Uses custom renderer below
            maintenance_date: { name: 'Date', withSort: true, maxWidth: '200px' },
        },
        actions: [
            {
                label: 'Edit Record',
                action: 'Edit',
                icon: 'Edit',
                can_do: access.can_edit,
            }
        ],
        dataSource: `/trucks/${id}/maintenance-history`, 
        primaryKey: 'id',
        //editPath: (recordId: any) => `/fleet-management/maintenance-history/${recordId}/edit`,
        editPath: (recordId: any) => `/fleet-management/maintenance-history/${id}/${recordId}`,
        softDelete: true,
        displayInModal: true,
        noRecordText: 'No maintenance records found',
        showSelection: false,
    }), [id, access.can_edit]);

    // Update onOpenModal as well just in case
    const handleEdit = (row: any) => {
            // 1. Get the Maintenance Record ID
            const recordId = typeof row === 'object' ? row.id : row;
            
            // 2. 'id' comes from your useParams() at the top of the page (the Truck ID)
            // We navigate to /truckId/recordId to match the /:id/:id permission
            if (id && recordId) {
                navigate(`/fleet-management/maintenance-history/${id}/${recordId}`);
            } else {
                console.error("Missing IDs for navigation:", { truckId: id, recordId });
            }
        };

    const handleSearch = () => {
        const query = searchRef.current?.value || '';
        setSearchQuery(query); 
        tableRef.current?.applyFilter('search', query); 
    };

    // return (
    //     <AdminLayout>
    //         <Box p={4} h="100%" display="flex" flexDirection="column">
    //             <VStack spacing={4} align="stretch" flex="1" minH={0}>
    //                 <Card display="flex" flexDirection="column" flex="1" minH={0} overflow="hidden">
                        
    //                     {/* 🌟 Removed Back Button from Header */}
    //                     <CardHeader py={3}>
    //                         <Box>
    //                             <Heading size="md" fontWeight="bold">Maintenance History: {truckPlate}</Heading>
    //                             <Text fontSize="sm" color={colors.text}>History of parts and services for this vehicle.</Text>
    //                         </Box>
    //                     </CardHeader>
                        
    //                     <CardHeader py={0} px={6}>
    //                         <HStack justify="space-between" align="center" width="100%" pb={4}>
    //                             <Box flex="0 1 auto" minW="250px" maxW="400px">
    //                                 <InputGroup size="sm">
    //                                     <Input
    //                                         ref={searchRef}
    //                                         placeholder="Search records..."
    //                                         onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
    //                                         defaultValue={searchQuery}
    //                                     />
    //                                     <InputRightElement width="2.5rem">
    //                                         <IconButton
    //                                             aria-label="Search"
    //                                             icon={<Search size={16} />}
    //                                             size="sm"
    //                                             variant="ghost"
    //                                             onClick={handleSearch}
    //                                         />
    //                                     </InputRightElement>
    //                                 </InputGroup>
    //                             </Box>

    //                             <Box pb={2}>
    //                                 <ActionButtons
    //                                     pathname={`/fleet-management/maintenance-history/${id}`}
    //                                     createLabel="Add Article"
    //                                 />
    //                             </Box>
    //                         </HStack>
    //                     </CardHeader>
                        
    //                     <CardBody p={0} mx={6} flex="1" display="flex" flexDirection="column" overflow="hidden" sx={{
    //                             'th:first-of-type, td:first-of-type': {
    //                                 display: 'none',
    //                             }
    //                         }}>
    //                         <Box h="100%">
    //                             <DataTable
    //                                 ref={tableRef}
    //                                 options={tableOptions}
    //                                 onOpenModal={handleEdit}
    //                                 access={{ can_edit: access.can_edit, can_create: access.can_create, can_delete: access.can_delete }}
    //                                 customRenderers={{
    //                                     maintenance_date: renderFormattedDate,
    //                                     total: renderTotalCost, // 🌟 Fixed blank column
    //                                 }}
    //                                 // onOpenModal={(row) => {
    //                                 //     // const recordId = typeof row === 'object' ? row.id : row;
    //                                 //     // const targetUrl = `/fleet-management/maintenance-history/${recordId}/edit`;
    //                                 //     // console.log("Navigating to:", targetUrl); // Check this in your browser console
    //                                 //     // navigate(targetUrl);
    //                                 //     // //navigate(`/fleet-management/maintenance-history/${id}/${recordId}/edit`);
    //                                 //     const recordId = typeof row === 'object' ? row.id : row;
    //                                 //     // 'id' is the truckId from the List Page's useParams
    //                                 //     navigate(`/fleet-management/maintenance-history/${id}/${recordId}/edit`);
                    
    //                                 // }}
    //                             />
    //                         </Box>
    //                     </CardBody>
    //                 </Card>
    //             </VStack> 
    //         </Box> 
    //     </AdminLayout>
    // );
    return (
        <AdminLayout>
            <Box p={4} h="100%" display="flex" flexDirection="column">
                <VStack spacing={4} align="stretch" flex="1" minH={0}>
                    <Card display="flex" flexDirection="column" flex="1" minH={0} overflow="hidden">
                        
                        {/* 🌟 Updated Header with Back Arrow */}
                        <CardHeader py={3} px={6}>
                            <HStack spacing={3} align="center">
                                <IconButton
                                    aria-label="Go back"
                                    icon={<Icon as={ChevronLeft} boxSize={6} />}
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => navigate('/fleet-management/trucks')} // 🌟 Adjust path as needed
                                    _hover={{ bg: 'gray.100' }}
                                />
                                <Box>
                                    <Heading size="md" fontWeight="bold">
                                        Maintenance History: {truckPlate}
                                    </Heading>
                                    <Text fontSize="sm" color="gray.500">
                                        History of parts and services for this vehicle.
                                    </Text>
                                </Box>
                            </HStack>
                        </CardHeader>
                        
                        {/* Search and Action Buttons Section */}
                        <CardHeader py={0} px={6}>
                            <HStack justify="space-between" align="center" width="100%" pb={4}>
                                <Box flex="0 1 auto" minW="250px" maxW="400px">
                                    <InputGroup size="sm">
                                        <Input
                                            ref={searchRef}
                                            placeholder="Search records..."
                                            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                                            defaultValue={searchQuery}
                                        />
                                        <InputRightElement width="2.5rem">
                                            <IconButton
                                                aria-label="Search"
                                                icon={<Search size={16} />}
                                                size="sm"
                                                variant="ghost"
                                                onClick={handleSearch}
                                            />
                                        </InputRightElement>
                                    </InputGroup>
                                </Box>

                                <Box pb={2}>
                                    <ActionButtons
                                        pathname={`/fleet-management/maintenance-history/${id}`}
                                        createLabel="Add Article"
                                    />
                                </Box>
                            </HStack>
                        </CardHeader>
                        
                        <CardBody p={0} mx={6} flex="1" display="flex" flexDirection="column" overflow="hidden" sx={{
                                'th:first-of-type, td:first-of-type': {
                                    display: 'none',
                                }
                            }}>
                            <Box h="100%">
                                <DataTable
                                    ref={tableRef}
                                    options={tableOptions}
                                    onOpenModal={handleEdit}
                                    access={{ can_edit: access.can_edit, can_create: access.can_create, can_delete: access.can_delete }}
                                    customRenderers={{
                                        maintenance_date: renderFormattedDate,
                                        total: renderTotalCost, 
                                    }}
                                />
                            </Box>
                        </CardBody>
                    </Card>
                </VStack> 
            </Box> 
        </AdminLayout>
    );
};

export default MaintenanceHistoryPage;